import type { IPRAttributes } from '@upright/common/model/input/companies/products/ipr';
import { getAllEnumValues } from '@upright/common/utils';
import { Dictionary } from 'lodash';
import { TemplateTrace } from './templates/types';

export interface ProductMixin {
  productCode: string;
  expression: string;
  title: string;
  createdBy: string;
  createdAt: string;
  template?: TemplateTrace | null;
}

export enum MixinOperandType {
  PRODUCT = 'product',
  NUMBER = 'number',
  ATTRIBUTE = 'attribute',
  TEMPLATE_ARGUMENT = 'template-argument',
}

export const allMixinOperands = getAllEnumValues(MixinOperandType, 'string');

export enum LegacyOperationType {
  IN_DEVELOPING_COUNTRIES = 'in-developing',
  BIO_SUSTAINABLE = 'bio-sustainable',
  SUSTAINABLE_BUILDINGS = 'sustainable-buildings',
  SUSTAINABLE_FORESTRY = 'sustainable-forestry',
  INVESTING_IN = 'investing-in',
  LENDING_FOR = 'lending-for',
}
// Keep in sync with mixin.jison
// Keys are expected to equal values (except for case and hyphen)
export enum MixinOperationType {
  V2_FOR = 'v2-for',
  V2_USING = 'v2-using',
  RETAIL_OF = 'retail-of',
  V2_INVESTING_IN = 'v2-investing-in',
  SCALE_BY = 'scale-by',
  ADD_BY = 'add-by',
  IN_DEVELOPING_COUNTRIES = 'in-developing',
  SUSTAINABLE_BUILDINGS = 'sustainable-buildings',
  SUSTAINABLE_FORESTRY = 'sustainable-forestry',
  INVESTING_IN = 'investing-in',
  LENDING_FOR = 'lending-for',
  MIXIN = 'mixin',
  ALIAS = 'alias',
  USING_RATHER_THAN = 'using-rather-than',
  FOR_RATHER_THAN = 'for-rather-than',
  AND = 'and',
}

export const allMixinOperators = getAllEnumValues(MixinOperationType, 'string');
export const attributeMixins = [MixinOperationType.ADD_BY, MixinOperationType.SCALE_BY];
export const valueChainMixins = [
  MixinOperationType.V2_FOR,
  MixinOperationType.V2_USING,
  MixinOperationType.RETAIL_OF,
  MixinOperationType.USING_RATHER_THAN,
  MixinOperationType.FOR_RATHER_THAN,
  MixinOperationType.V2_INVESTING_IN,
];

export const leftHandedMixins = [MixinOperationType.IN_DEVELOPING_COUNTRIES, MixinOperationType.ALIAS];
export const rightHandedMixins = [MixinOperationType.SUSTAINABLE_BUILDINGS, MixinOperationType.SUSTAINABLE_FORESTRY];

export function isMixinOperationType(input: string): boolean {
  return Object.values(MixinOperationType).includes(input as MixinOperationType);
}

export function isMixinOperandType(input: string): boolean {
  return Object.values(MixinOperandType).includes(input as MixinOperandType);
}

export function isLegacyOperation(input: string): boolean {
  return Object.values(LegacyOperationType).includes(input as LegacyOperationType);
}

export function isAttributeMixin(input: string | MixinOperationType): boolean {
  return attributeMixins.includes(input as MixinOperationType);
}

export function friendlyMixinOperation(operation: MixinOperationType) {
  switch (operation) {
    case MixinOperationType.V2_FOR:
      return '{0} for {1}';
    case MixinOperationType.V2_USING:
      return '{0} using {1}';
    case MixinOperationType.V2_INVESTING_IN:
    case MixinOperationType.INVESTING_IN:
      return '{0} investing in {1}';
    case MixinOperationType.RETAIL_OF:
      return '{0} of {1}';
    case MixinOperationType.LENDING_FOR:
      return '{0} lending for {1}';
    case MixinOperationType.IN_DEVELOPING_COUNTRIES:
      return '{0} in developing countries';
    case MixinOperationType.SUSTAINABLE_FORESTRY:
      return 'sustainable {0}';
    case MixinOperationType.SUSTAINABLE_BUILDINGS:
      return 'energy efficient {0}';
    case MixinOperationType.MIXIN:
      return 'all operations';
    case MixinOperationType.USING_RATHER_THAN:
      return '{0} using {1} rather than {2}';
    case MixinOperationType.FOR_RATHER_THAN:
      return '{0} for {1} rather than {2}';
    case MixinOperationType.ADD_BY:
      return '{0} add {1} by {2}';
    case MixinOperationType.SCALE_BY:
      return '{0} scale {1} by {2}';
    case MixinOperationType.ALIAS:
      return '{0}';
    case MixinOperationType.AND:
      return '{0} and {1}';
    default:
      return operation;
  }
}

export type MixinElementType = MixinOperandType | MixinOperationType;

export interface MixinOperand {
  type: MixinOperandType;
  arguments: [string];
}

export interface MixinOperation {
  type: MixinOperationType;
  arguments: Array<MixinOperation | MixinOperand>;
}

export interface MixinOptionType {
  label: string;
  value: MixinOperationType | undefined | null;
}

export type MixinSyntaxTree = MixinOperand | MixinOperation;

/**
 * Alternative way to flexibly represent or handle mixin data with mostly in the UI
 * for example: src/ui/views/internal/mixins-view/sdg-argument/sdg-weight-table.tsx
 */
export interface CompactMixin {
  leftExpression: string;
  rightExpression: string | undefined;
  operator: string;
  mixinId: string; // Branch hierarchy identifier
  parent: string | undefined;
  children?: string[] | undefined;
}

export type FlattenedMixinSyntaxTree = Dictionary<CompactMixin>;

export type CreateMixinResult = Pick<ProductMixin, 'productCode'>;

export interface MixinInformationRecord extends Omit<ProductMixin, 'netImpactRatio' | 'createdBy'> {
  templateIsArchived: boolean | undefined;
  /** True for mixins the APG IPRA flow creates (`<BASE> alias` with an IPR hash). */
  isIpra: boolean;
  /** The IPR attributes applied to an IPRA mixin; undefined for other mixins. */
  ipra: Partial<IPRAttributes> | undefined;
  /** IPR attribute keys a person set by hand on an IPRA mixin; undefined for other mixins. */
  ipraManualFields: string[] | undefined;
}
